import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import {
  BookOpen,
  CheckCircle2,
  Clock3,
  X,
  XCircle
} from 'lucide-react';

import {
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';

import { db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/auth/AuthProvider';

function toMillis(value) {
  if (!value) return 0;

  if (typeof value?.toMillis === 'function') {
    return value.toMillis();
  }

  const parsed = new Date(value).getTime();

  return Number.isFinite(parsed)
    ? parsed
    : 0;
}

function formatDeadline(value) {
  if (!value) {
    return 'Date non définie';
  }

  const date = new Date(`${value}T12:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(
    'fr-TN',
    {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }
  ).format(date);
}

export default function HomeworkValidationPage() {
  const {
    user,
    profile,
    isGerant
  } = useAuth();

  const [homework, setHomework] = useState([]);
  const [loading, setLoading] = useState(true);

  const [processingId, setProcessingId] =
    useState('');

  const [rejectingId, setRejectingId] =
    useState('');

  const [rejectionReason, setRejectionReason] =
    useState('');

  const [error, setError] =
    useState('');

  const [notice, setNotice] =
    useState('');

  const managerName =
    profile?.fullname ||
    profile?.fullName ||
    profile?.displayName ||
    'Gérant';

  // =============================================
  // CHARGEMENT DES TRAVAUX EN ATTENTE
  // =============================================

  useEffect(() => {
    if (!user?.uid || !isGerant) {
      return;
    }

    let cancelled = false;

    async function loadPendingHomework() {
      setLoading(true);
      setError('');

      try {
        const homeworkQuery = query(
          collection(db, 'homework'),
          where('status', '==', 'pending')
        );

        const snapshot =
          await getDocs(homeworkQuery);

        if (cancelled) {
          return;
        }

        const rows = snapshot.docs
          .map((item) => ({
            id: item.id,
            ...item.data()
          }))
          .sort(
            (a, b) =>
              toMillis(
                b.submittedAt ||
                b.updatedAt ||
                b.createdAt
              ) -
              toMillis(
                a.submittedAt ||
                a.updatedAt ||
                a.createdAt
              )
          );

        setHomework(rows);
      } catch (loadError) {
        console.error(
          'Erreur chargement travaux à valider :',
          loadError
        );

        if (!cancelled) {
          setError(
            'Impossible de charger les travaux à valider.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadPendingHomework();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, isGerant]);

  // =============================================
  // VALIDER
  // =============================================

  async function approveHomework(item) {
    const confirmed = window.confirm(
      `Valider le travail « ${item.title || 'Sans titre'} » ?`
    );

    if (!confirmed) {
      return;
    }

    setProcessingId(item.id);
    setError('');
    setNotice('');

    try {
      await updateDoc(
        doc(
          db,
          'homework',
          item.id
        ),
        {
          status: 'approved',

          approvedAt:
            serverTimestamp(),

          approvedByUid:
            user.uid,

          approvedByName:
            managerName,

          // Supprime un éventuel ancien refus.
          rejectedAt:
            deleteField(),

          rejectedByUid:
            deleteField(),

          rejectedByName:
            deleteField(),

          rejectionReason:
            deleteField(),

          updatedAt:
            serverTimestamp()
        }
      );

      setHomework((current) =>
        current.filter(
          (row) =>
            row.id !== item.id
        )
      );

      setNotice(
        `Le travail « ${item.title} » a été validé. Il est maintenant visible par les parents.`
      );
    } catch (approveError) {
      console.error(
        'Erreur validation travail :',
        approveError
      );

      setError(
        'Impossible de valider ce travail.'
      );
    } finally {
      setProcessingId('');
    }
  }

  // =============================================
  // OUVRIR LE REFUS
  // =============================================

  function startReject(item) {
    setRejectingId(item.id);
    setRejectionReason('');
    setError('');
    setNotice('');
  }

  function cancelReject() {
    setRejectingId('');
    setRejectionReason('');
  }

  // =============================================
  // REFUSER
  // =============================================

  async function rejectHomework(item) {
    const reason =
      rejectionReason.trim();

    if (!reason) {
      setError(
        'Indiquez le motif du refus.'
      );

      return;
    }

    setProcessingId(item.id);
    setError('');
    setNotice('');

    try {
      await updateDoc(
        doc(
          db,
          'homework',
          item.id
        ),
        {
          status: 'rejected',

          rejectionReason:
            reason,

          rejectedAt:
            serverTimestamp(),

          rejectedByUid:
            user.uid,

          rejectedByName:
            managerName,

          approvedAt:
            deleteField(),

          approvedByUid:
            deleteField(),

          approvedByName:
            deleteField(),

          updatedAt:
            serverTimestamp()
        }
      );

      setHomework((current) =>
        current.filter(
          (row) =>
            row.id !== item.id
        )
      );

      setRejectingId('');
      setRejectionReason('');

      setNotice(
        `Le travail « ${item.title} » a été refusé. L’enseignant pourra voir le motif et le corriger.`
      );
    } catch (rejectError) {
      console.error(
        'Erreur refus travail :',
        rejectError
      );

      setError(
        'Impossible de refuser ce travail.'
      );
    } finally {
      setProcessingId('');
    }
  }

  // =============================================
  // SÉCURITÉ INTERFACE
  // =============================================

  if (!isGerant) {
    return (
      <Navigate
        to="/403"
        replace
      />
    );
  }

  return (
    <main className="page">
      <section className="page-header">
        <div>
          <p className="eyebrow">
            VALIDATION PÉDAGOGIQUE
          </p>

          <h1>
            Travaux à valider
          </h1>

          <p className="muted">
            Vérifiez les travaux soumis par les
            enseignants avant leur transmission
            aux parents.
          </p>
        </div>
      </section>

      {error && (
        <div
          className="alert alert-error"
          style={{
            marginBottom: '18px'
          }}
        >
          {error}
        </div>
      )}

      {notice && (
        <div
          style={{
            marginBottom: '18px',
            padding: '14px',
            borderRadius: '12px',
            background: '#dcfce7'
          }}
        >
          {notice}
        </div>
      )}

      {loading ? (
        <section className="panel">
          <p className="muted">
            Chargement des travaux…
          </p>
        </section>
      ) : homework.length === 0 ? (
        <section className="panel">
          <CheckCircle2
            size={34}
          />

          <h2>
            Aucun travail en attente
          </h2>

          <p className="muted">
            Tous les travaux soumis ont été traités.
          </p>
        </section>
      ) : (
        <div
          style={{
            display: 'grid',
            gap: '18px'
          }}
        >
          {homework.map((item) => (
            <article
              key={item.id}
              className="panel"
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent:
                    'space-between',
                  alignItems:
                    'flex-start',
                  gap: '18px',
                  flexWrap: 'wrap'
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minWidth: '250px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span
                      style={{
                        display:
                          'inline-flex',
                        alignItems:
                          'center',
                        gap: '5px',
                        padding:
                          '5px 10px',
                        borderRadius:
                          '999px',
                        background:
                          '#fef3c7',
                        fontSize:
                          '13px',
                        fontWeight: 700
                      }}
                    >
                      <Clock3
                        size={14}
                      />

                      En attente
                    </span>

                    <span
                      style={{
                        display:
                          'inline-block',
                        padding:
                          '5px 10px',
                        borderRadius:
                          '999px',
                        background:
                          '#eef4ff',
                        fontSize:
                          '13px',
                        fontWeight: 700
                      }}
                    >
                      {item.className ||
                        item.classId}
                      {' • '}
                      {item.subjectName ||
                        item.subject ||
                        item.subjectId}
                    </span>
                  </div>

                  <h2
                    style={{
                      margin:
                        '14px 0 6px'
                    }}
                  >
                    {item.title ||
                      'Travail à faire'}
                  </h2>

                  <p
                    className="muted"
                    style={{
                      whiteSpace:
                        'pre-wrap'
                    }}
                  >
                    {item.instructions ||
                      item.description ||
                      'Aucune consigne.'}
                  </p>

                  <div
                    style={{
                      marginTop:
                        '18px',
                      display: 'grid',
                      gap: '7px'
                    }}
                  >
                    <div>
                      <strong>
                        Enseignant :
                      </strong>{' '}
                      {item.teacherName ||
                        'Non renseigné'}
                    </div>

                    <div>
                      <strong>
                        Classe :
                      </strong>{' '}
                      {item.className ||
                        item.classId}
                    </div>

                    <div>
                      <strong>
                        Matière :
                      </strong>{' '}
                      {item.subjectName ||
                        item.subject ||
                        item.subjectId}
                    </div>

                    <div>
                      <strong>
                        À rendre :
                      </strong>{' '}
                      {formatDeadline(
                        item.deadline
                      )}
                    </div>
                  </div>
                </div>

                <BookOpen
                  size={36}
                />
              </div>

              {/* REFUS */}

              {rejectingId ===
                item.id && (
                <div
                  style={{
                    marginTop:
                      '20px',
                    padding:
                      '16px',
                    borderRadius:
                      '12px',
                    background:
                      '#fff7ed'
                  }}
                >
                  <label>
                    Motif du refus

                    <textarea
                      value={
                        rejectionReason
                      }
                      onChange={(
                        event
                      ) =>
                        setRejectionReason(
                          event.target
                            .value
                        )
                      }
                      placeholder="Expliquez ce que l’enseignant doit corriger."
                      rows={4}
                      required
                    />
                  </label>

                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      marginTop:
                        '12px',
                      flexWrap: 'wrap'
                    }}
                  >
                    <button
                      type="button"
                      className="button button-primary"
                      disabled={
                        processingId ===
                        item.id
                      }
                      onClick={() =>
                        rejectHomework(
                          item
                        )
                      }
                    >
                      <XCircle
                        size={17}
                      />

                      Confirmer le refus
                    </button>

                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={
                        cancelReject
                      }
                    >
                      <X
                        size={17}
                      />

                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {/* ACTIONS */}

              {rejectingId !==
                item.id && (
                <div
                  style={{
                    marginTop:
                      '22px',
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap',
                    justifyContent:
                      'flex-end'
                  }}
                >
                  <button
                    type="button"
                    className="button button-secondary"
                    disabled={
                      processingId ===
                      item.id
                    }
                    onClick={() =>
                      startReject(
                        item
                      )
                    }
                  >
                    <XCircle
                      size={17}
                    />

                    Refuser
                  </button>

                  <button
                    type="button"
                    className="button button-primary"
                    disabled={
                      processingId ===
                      item.id
                    }
                    onClick={() =>
                      approveHomework(
                        item
                      )
                    }
                  >
                    <CheckCircle2
                      size={17}
                    />

                    {processingId ===
                    item.id
                      ? 'Validation…'
                      : 'Valider'}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </main>
  );
}